import { ProductItemWithDetails, Product, Location, QuantityUnit, CurrentStockResponse } from "./grocyApi.ts";
import { arrayToMap } from "./arrayUtils.ts";

/**
 * Creates a map of barcodes to product IDs for quick lookup
 */
export function createBarcodeMap(barcodesData: { barcode: string; product_id: number }[] | undefined): Map<string, number> {
  if (!barcodesData) return new Map();
  
  const map = new Map<string, number>();
  barcodesData.forEach(item => {
    map.set(item.barcode, item.product_id);
  });
  return map;
}

/**
 * Creates a map of product IDs to product items with full details
 * @param includeZeroStock - When true, includes all products regardless of stock level. 
 *                          When false (default), only includes products with stock > 0.
 */
export function createProductItemsMap(
  stockData: CurrentStockResponse[] | undefined,
  productsData: Product[] | undefined,
  locationsData: Location[] | undefined,
  unitsData: QuantityUnit[] | undefined,
  includeZeroStock: boolean = false
): Map<number, ProductItemWithDetails> {
  if (!stockData || !productsData || !locationsData || !unitsData) {
    return new Map();
  }

  // Create lookup maps using the utility function
  const unitsMap = arrayToMap(unitsData, unit => unit.id);
  const stockMap = arrayToMap(stockData, stock => stock.product_id);

  const map = new Map<number, ProductItemWithDetails>();

  // Iterate over all products and apply stock filtering based on includeZeroStock flag
  productsData.forEach((product: Product) => {
    const location = locationsData.find((l: Location) => l.id === product.location_id);
    const quantityUnit = unitsMap.get(product.qu_id_stock);
    const stock = stockMap.get(product.id) || { product_id: product.id, amount: 0 } as CurrentStockResponse;
    
    // Only proceed if we have all required data
    if (location && quantityUnit && (includeZeroStock || stock.amount > 0)) {
      map.set(product.id, {
        stock,
        product,
        quantityUnit,
        location,
      });
    }
  });

  return map;
}

/**
 * Result type for barcode processing
 */
export type BarcodeProcessResult = 
  | { type: 'found', productItem: ProductItemWithDetails, barcode: string }
  | { type: 'no-stock', productItem: ProductItemWithDetails, barcode: string }
  | { type: 'not-found', barcode: string };

/**
 * Finds a product ID by trying different barcode variations
 * Handles leading zero mismatches between scanned and stored barcodes
 */
export function findProductId(
  searchBarcode: string,
  barcodeMap: Map<string, number>
): number | undefined {
  // Try exact match first
  let productId = barcodeMap.get(searchBarcode);
  if (productId) return productId;
  
  // Try with leading zero added
  productId = barcodeMap.get('0' + searchBarcode);
  if (productId) return productId;
  
  // Try with leading zeros removed
  const withoutLeadingZeros = searchBarcode.replace(/^0+/, '');
  if (withoutLeadingZeros && withoutLeadingZeros !== searchBarcode) {
    productId = barcodeMap.get(withoutLeadingZeros);
    if (productId) return productId;
  }
  
  return undefined;
}

/**
 * Processes a scanned barcode and returns the result
 * Handles leading zero variations in barcodes
 */
export function processScannedBarcode(
  barcode: string,
  barcodeMap: Map<string, number>,
  productItemsMap: Map<number, ProductItemWithDetails>
): BarcodeProcessResult {
  // Look up product by barcode with variations
  const productId = findProductId(barcode, barcodeMap);
  
  if (!productId) {
    return { type: 'not-found', barcode };
  }
  
  // Find in product data
  const productItem = productItemsMap.get(productId);
  
  if (!productItem) {
    // Product ID found but product not in map - treat as not found
    return { type: 'not-found', barcode };
  }
  
  // Product exists - check if it has stock
  if (productItem.stock.amount > 0) {
    return { type: 'found', productItem, barcode };
  } else {
    return { type: 'no-stock', productItem, barcode };
  }
} 